import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  PageHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/molecules';
import { PageLoader, Paragraph } from '@/components/atoms';
import { SummaryCards } from './components/summary-cards';
import { TransactionFilters } from './components/transaction-filters';
import { TransactionsTable } from './components/transactions-table';
import { TransactionDetailModal } from './components/transaction-detail-modal';
import type { TransactionUI } from './types';
import { fetchTransactions } from '@/api/queries/transactions/request';
import { transformTransactions } from './utils/transformTransaction';
import { formatCurrency } from '@/lib/helpers';

export const TransactionsPage = () => {
  const [tab, setTab] = useState<'total' | 'history'>('total');
  const [dateRange, setDateRange] = useState('today');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] =
    useState<TransactionUI | null>(null);

  const {
    data: response,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['transactions', { status: statusFilter, search: searchQuery }],
    queryFn: () =>
      fetchTransactions({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: searchQuery,
      }),
  });

  const transactions = useMemo(
    () => transformTransactions(response?.data ?? []),
    [response]
  );

  const filteredTransactions = useMemo(() => {
    if (tab === 'total') return transactions;
    return transactions.map((transaction, index) => ({
      ...transaction,
      id: `history-${index}`,
      refId: `Settle-${index + 510}`,
    }));
  }, [transactions, tab]);

  const { totalAmount, pendingAmount } = useMemo(() => {
    const parse = (t: TransactionUI) =>
      parseFloat(String(t.amount || '0').replace(/[^\d.-]/g, '')) || 0;
    const successTotal = transactions
      .filter((t) => t.status === 'success')
      .reduce((s, t) => s + parse(t), 0);
    const pendingTotal = transactions
      .filter((t) => t.status === 'pending')
      .reduce((s, t) => s + parse(t), 0);
    return {
      totalAmount: formatCurrency(successTotal),
      pendingAmount: formatCurrency(pendingTotal),
    };
  }, [transactions]);

  const handleViewTransaction = (transaction: TransactionUI) => {
    setSelectedTransaction(transaction);
    setDetailModalOpen(true);
  };

  // Loading and error handling
  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader
          heading="Transactions"
          description="Track your settlements, payouts, and transaction statuses in real time."
        />
        <PageLoader message="Loading transactions..." />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader
          heading="Transactions"
          description="Track your settlements, payouts, and transaction statuses in real time."
        />
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <p className="text-red-500 mb-2">Failed to load transactions</p>
            <button
              className="underline text-primary"
              onClick={() => refetch()}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        heading="Transactions"
        description="Track your settlements, payouts, and transaction statuses in real time."
      />

      <SummaryCards totalAmount={totalAmount} pendingAmount={pendingAmount} />

      <Paragraph className="text-sm text-muted-foreground">
        All pending settlements are paid to your account within 24 hours.
      </Paragraph>

      <Tabs value={tab} onValueChange={(value) => setTab(value as typeof tab)}>
        <TabsList className="bg-muted/50 mt-12">
          <TabsTrigger value="total">Total transaction</TabsTrigger>
          {/* <TabsTrigger value="history">Settlement history</TabsTrigger> */}
        </TabsList>

        <TabsContent value="total" className="lg:-mt-16 space-y-4">
          <TransactionFilters
            dateRange={dateRange}
            statusFilter={statusFilter}
            searchQuery={searchQuery}
            onDateRangeChange={setDateRange}
            onStatusFilterChange={setStatusFilter}
            onSearchChange={setSearchQuery}
          />
          <TransactionsTable
            data={filteredTransactions}
            onView={handleViewTransaction}
          />
        </TabsContent>

        {/* <TabsContent value="history" className="lg:-mt-16 space-y-4">
          <TransactionFilters
            dateRange={dateRange}
            statusFilter={statusFilter}
            searchQuery={searchQuery}
            onDateRangeChange={setDateRange}
            onStatusFilterChange={setStatusFilter}
            onSearchChange={setSearchQuery}
          />
          <TransactionsTable
            data={filteredTransactions}
            onView={handleViewTransaction}
          />
        </TabsContent> */}
      </Tabs>

      <TransactionDetailModal
        onClose={() => setDetailModalOpen(false)}
        transaction={selectedTransaction}
        open={detailModalOpen}
        onOpenChange={(open) => setDetailModalOpen(open)}
      />
    </div>
  );
};
